import React, { useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel
} from 'reactflow';
import dagre from '@dagrejs/dagre';
import 'reactflow/dist/style.css';
import CorporateEntityNode from './nodes/CorporateEntityNode';
import './CorporateGroupGraph.css';

const NODE_WIDTH = 240;
const NODE_HEIGHT = 170;

const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'TB',
    nodesep: 60,
    ranksep: 80,
    marginx: 40,
    marginy: 40,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
      targetPosition: 'top',
      sourcePosition: 'bottom',
    };
  });

  return { nodes: layoutedNodes, edges };
};

const CorporateGroupGraph = ({ graphData, onNodeClick, selectedEntityId }) => {
  const nodeTypes = useMemo(() => ({
    corporateEntity: CorporateEntityNode,
  }), []);

  const edgeDefaults = useMemo(() => ({
    type: 'smoothstep',
    animated: false,
    style: { stroke: '#9ca3af', strokeWidth: 2 },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: '#9ca3af',
      width: 16,
      height: 16,
    },
  }), []);

  const processedData = useMemo(() => {
    if (!graphData) return { nodes: [], edges: [] };

    const nodes = graphData.nodes.map(node => ({
      ...node,
      type: 'corporateEntity',
      data: {
        ...node.data,
        isSelected: String(node.data.id) === String(selectedEntityId),
      },
    }));

    const edges = graphData.edges.map(edge => ({
      ...edge,
      ...edgeDefaults,
    }));

    return getLayoutedElements(nodes, edges);
  }, [graphData, selectedEntityId, edgeDefaults]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    setNodes(processedData.nodes);
    setEdges(processedData.edges);
  }, [processedData, setNodes, setEdges]);

  const handleNodeClick = useCallback((event, node) => {
    if (onNodeClick) {
      onNodeClick(node.data);
    }
  }, [onNodeClick]);

  const consolidationIds = graphData?.consolidation_ids || [];
  const hasConsolidation = consolidationIds.length > 0;

  return (
    <div className="sg-graph-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
      >
        <Background color="#e5e7eb" gap={20} size={1} />
        <Controls
          showInteractive={false}
          className="sg-flow-controls"
        />
        <MiniMap
          nodeColor={(node) => {
            if (node.data?.in_consolidation_circle) return '#16a34a';
            return node.data?.node_color || '#4a6b25';
          }}
          className="sg-minimap"
          maskColor="rgba(0, 0, 0, 0.06)"
        />
        {hasConsolidation && (
          <Panel position="top-left" className="sg-consolidation-legend">
            <div className="sg-legend-item">
              <span className="sg-legend-dot sg-legend-consolidation"></span>
              <span>Consolidation Circle</span>
            </div>
            <div className="sg-legend-item">
              <span className="sg-legend-dot sg-legend-standard"></span>
              <span>Independent Entity</span>
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
};

export default CorporateGroupGraph;
